import { StateNode, TLPointerEventInfo, TLShapeId, Vec, createShapeId, isShapeId } from "@tldraw/editor";
import { CustomTextUtilType } from "../CustomTextUtil";

export class Pointing extends StateNode {
  static id = "pointing";

  shape?: CustomTextUtilType;

  markId = "";

  enterTime = 0;
  onEnter(): void {
    this.enterTime = Date.now();
  }

  onExit() {
    this.editor.setHintingShapes([]);
  }

  onPointerMove(info: TLPointerEventInfo) {
    // Create a fixed width shape if the user wants to do that.

    // Don't create a fixed width shape unless the the drag is a little larger,
    // otherwise you get a vertical column of single characters if you accidentally
    // drag a bit unintentionally.

    // If the user hasn't been pointing for more than 150ms, don't create a fixed width shape
    if (Date.now() - this.enterTime < 150) return;

    const { editor } = this;
    const { isPointing } = editor.inputs;

    if (!isPointing) return;

    const { originPagePoint, currentPagePoint } = editor.inputs;

    const currentDragDist = Math.abs(originPagePoint.x - currentPagePoint.x);

    const baseMinDragDistForFixedWidth = Math.sqrt(
      editor.getInstanceState().isCoarsePointer
        ? editor.options.coarseDragDistanceSquared
        : editor.options.dragDistanceSquared
    );

    // Ten times the base drag distance for fixed width
    const minSquaredDragDist = (baseMinDragDistForFixedWidth * 6) / editor.getZoomLevel();

    if (currentDragDist > minSquaredDragDist) {
      const id = createShapeId();
      this.markId = editor.markHistoryStoppingPoint(`creating_text:${id}`);

      // create the initial shape with the width that we've dragged
      const shape = this.createTextShape(id, originPagePoint, false, currentDragDist);

      if (!shape) {
        this.cancel();
        return;
      }

      // Now save the fresh reference
      this.shape = editor.getShape(shape);

      editor.select(id);

      editor.setCurrentTool("select.resizing", {
        ...info,
        target: "selection",
        handle: "right",
        isCreating: true,
        creatingMarkId: this.markId,
        // Make sure the cursor offset takes into account how far we've already dragged
        creationCursorOffset: { x: currentDragDist, y: 1 },
        onInteractionEnd: "text",
        onCreate: () => {
          editor.setEditingShape(shape.id);
          // this will automatically set the state to 'select.editing_shape'
        },
      });
    }
  }

  onPointerUp() {
    this.complete();
  }

  onComplete() {
    this.cancel();
  }

  onCancel() {
    this.cancel();
  }

  onInterrupt() {
    this.cancel();
  }

  private complete() {
    this.editor.markHistoryStoppingPoint("creating customText shape");
    const id = createShapeId();
    const { originPagePoint } = this.editor.inputs;
    const shape = this.createTextShape(id, originPagePoint, true, 20);
    if (!shape) return;

    this.editor.select(id);
    this.editor.setEditingShape(id);
    // this will automatically set the state to 'select.editing_shape'
  }

  private cancel() {
    this.parent.transition("idle");
    this.editor.bailToMark(this.markId);
  }

  private createTextShape(id: TLShapeId, point: Vec, autoSize: boolean, width: number) {
    const lastSelectedFont =
      typeof window !== "undefined" ? localStorage.getItem("lastSelectedFont") || "Inter" : "Inter";

    // Read brand default font preset (full style: size, lineHeight, letterSpacing)
    let brandPresetProps: Record<string, any> = {};
    if (typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem("brandDefaultFontPreset");
        if (raw) {
          const preset = JSON.parse(raw);
          brandPresetProps = {
            font: preset.fontVariant || preset.fontFamily || lastSelectedFont,
            size: preset.fontSize,
            lineHeight: preset.lineHeight,
            letterSpacing: preset.letterSpacing,
          };
        }
      } catch {
        // ignore localStorage errors
      }
    }

    this.editor.createShape<CustomTextUtilType>({
      id,
      type: "customText",
      x: point.x,
      y: point.y,
      props: {
        text: "",
        autoSize,
        w: width,
        font: lastSelectedFont,
        scale: this.editor.user.getIsDynamicResizeMode() ? 1 / this.editor.getZoomLevel() : 1,
        ...brandPresetProps,
      },
    });

    const shape = this.editor.getShape<CustomTextUtilType>(id);
    if (!shape) {
      this.cancel();
      return;
    }

    const bounds = this.editor.getShapePageBounds(shape)!;

    const delta = new Vec();

    if (autoSize) {
      switch (shape.props.textAlign) {
        case "start": {
          delta.x = 0;
          break;
        }
        case "middle": {
          delta.x = -bounds.width / 2;
          break;
        }
        case "end": {
          delta.x = -bounds.width;
          break;
        }
      }
    } else {
      delta.x = 0;
    }

    delta.y = -bounds.height / 2;

    if (isShapeId(shape.parentId)) {
      const transform = this.editor.getShapeParentTransform(shape);
      delta.rot(-transform.rotation());
    }

    this.editor.updateShape({
      ...shape,
      x: shape.x + delta.x,
      y: shape.y + delta.y,
    });

    return shape;
  }
}
